// assets/eos-sticky-sepet.js
import { Component } from '@theme/component';

const MOBILE_QUERY = '(max-width: 749px)';
const CHECK_INTERVAL_MS = 120;

/**
 * Mobile sticky add-to-cart bar.
 *
 * Visibility is driven by a throttled scroll listener rather than
 * IntersectionObserver, which does not fire reliably in every embedded
 * preview this theme is checked in. The bar never submits a form itself:
 * incomplete personalisation sends the shopper to the missing field, a
 * complete one clicks the theme's own add-to-cart button.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} buton
 * @property {HTMLElement} fiyat
 * @property {HTMLElement} ozet
 * @extends Component<Refs>
 */
class EosStickySepet extends Component {
  requiredRefs = ['buton', 'fiyat', 'ozet'];

  #complete = false;
  #lastCheck = 0;
  /** @type {number | null} */
  #trailing = null;
  #media = window.matchMedia(MOBILE_QUERY);
  /** @type {MutationObserver | null} */
  #priceObserver = null;
  /** Last value written to --eos-sticky-sepet-h, so an unchanged height costs no style write. */
  #publishedHeight = '';

  connectedCallback() {
    super.connectedCallback();
    // Horizon scrolls inside .page-wrapper on some layouts and the window on
    // others, and which one can change with the viewport, so listen to both.
    for (const target of this.#scrollers()) target.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    document.addEventListener('eos:kisisellestirme:change', this.#onPersonalise);

    const personaliser = this.#personaliser();
    // No personalisation block on this product means nothing blocks the cart.
    this.#complete = personaliser ? Boolean(/** @type {any} */ (personaliser).tamam) : true;

    this.#syncPrice();
    const price = this.#mainSection()?.querySelector('product-price');
    if (price) {
      this.#priceObserver = new MutationObserver(() => this.#syncPrice());
      this.#priceObserver.observe(price, { childList: true, subtree: true, characterData: true });
    }

    this.#renderButton();
    this.#check();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#gecilmedi(false);
    for (const target of this.#scrollers()) target.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    document.removeEventListener('eos:kisisellestirme:change', this.#onPersonalise);
    this.#priceObserver?.disconnect();
    this.#close();
  }

  tikla() {
    if (!this.#complete) {
      /* Adım adım akış varsa onu aç: müşteriyi uzun sayfada bir alana
         odaklamak yerine soruları sırayla sormak, çubuğun "Kişiselleştir"
         sözünü gerçekten karşılayan davranıştır. Sihirbaz kapalıysa eski
         davranış (alana odaklan) aynen kalır. */
      const sihirbaz = /** @type {any} */ (document.querySelector('eos-sihirbaz'));
      if (sihirbaz && typeof sihirbaz.ac === 'function') { sihirbaz.ac(this.refs?.buton); return; }
      const personaliser = /** @type {any} */ (this.#personaliser());
      personaliser?.odaklan?.();
      return;
    }
    this.#addToCartButton()?.click();
  }

  /** @returns {(Window | Element)[]} */
  #scrollers() {
    const wrapper = document.querySelector('.page-wrapper');
    return wrapper ? [window, wrapper] : [window];
  }

  #mainSection() {
    return this.#addToCartButton()?.closest('.shopify-section') ?? null;
  }

  #personaliser() {
    return document.querySelector('eos-kisisellestirme-component');
  }

  /** @returns {HTMLButtonElement | null} */
  #addToCartButton() {
    return document.querySelector('product-form-component [ref="addToCartButton"]');
  }

  /** @param {Event} event */
  #onPersonalise = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail ?? {};
    this.#complete = Boolean(detail.tamam);
    this.refs.ozet.textContent = detail.ozet ?? '';
    this.refs.ozet.hidden = !detail.ozet;
    this.#renderButton();
  };

  #onScroll = () => {
    const now = Date.now();
    if (now - this.#lastCheck >= CHECK_INTERVAL_MS) {
      this.#check();
      return;
    }
    if (this.#trailing === null) {
      this.#trailing = window.setTimeout(() => {
        this.#trailing = null;
        this.#check();
      }, CHECK_INTERVAL_MS);
    }
  };

  #check() {
    this.#lastCheck = Date.now();
    const button = this.#addToCartButton();
    // `data-masaustu` turns the bar on above 750 px too (owner's request:
    // "en alta yapışık Sepete Ekle" on every product page).
    const breakpointOk = this.#media.matches || this.dataset.masaustu === 'true';
    if (!breakpointOk || !button) {
      this.#anaDugme(false);
      this.#gecilmedi(false);
      this.#close();
      return;
    }

    const viewport = document.documentElement.clientHeight || window.innerHeight;
    const rect = button.getBoundingClientRect();
    // Measured zero means the layout is not ready; showing nothing is safer.
    if (viewport === 0 || (rect.width === 0 && rect.height === 0)) {
      this.#anaDugme(false);
      this.#gecilmedi(false);
      this.#close();
      return;
    }
    // Phone: the floating WhatsApp / chat / discount buttons stay away until
    // the shopper has scrolled past the real "Sepete ekle". Above it sit the
    // title, variant picker and the required personalisation fields, which
    // the buttons used to cover (CSS in sections/eos-sticky-sepet.liquid).
    this.#gecilmedi(rect.top > 0);
    // Floating WhatsApp / chat buttons step aside while the real button is on
    // screen (CSS in sections/eos-whatsapp.liquid and eos-danisman.liquid).
    this.#anaDugme(rect.bottom > 0 && rect.top < viewport);
    // Shown whenever the real button is off screen - above OR below. Measured
    // on a phone the real button sat at 1521 px, so a bar that waited for it
    // to be scrolled past left the first screens without any way to buy. While
    // the real button is visible the bar hides: never two buttons at once.
    // Incomplete personalisation turns the bar into "Kişiselleştir", which
    // opens the step-by-step flow instead of adding anything to the cart.
    // `data-her-zaman`: the bar stays on screen even while the real button is
    // visible - a permanent "Sepete ekle" at the bottom edge.
    const buttonOffScreen = this.dataset.herZaman === 'true' || rect.bottom < 0 || rect.top > viewport;

    // Near the footer the shopper is no longer shopping; do not cover the credit.
    const credit = document.querySelector('.ab__taftri') ?? document.querySelector('footer');
    const creditRect = credit?.getBoundingClientRect();
    const nearFooter = Boolean(creditRect && creditRect.top < viewport);

    if (!buttonOffScreen || nearFooter) {
      this.#close();
    } else {
      this.#open();
    }
  }

  #open() {
    if (this.hidden) {
      this.hidden = false;
      document.body.classList.add('eos-sticky-sepet-acik');
    }
    // Re-measured on every check, not only on first open: the height differs
    // between phone and desktop (resize, rotation), and the floating WhatsApp
    // button and guide launcher lift by exactly this value.
    this.#publishHeight(`${this.offsetHeight}px`);
  }

  #close() {
    if (this.hidden) return;
    this.hidden = true;
    document.body.classList.remove('eos-sticky-sepet-acik');
    this.#publishHeight('0px');
  }

  /** @param {boolean} gorunur */
  #anaDugme(gorunur) {
    if (document.documentElement.hasAttribute('data-eos-ana-atc') === gorunur) return;
    document.documentElement.toggleAttribute('data-eos-ana-atc', gorunur);
  }

  /** @param {boolean} gecilmedi */
  #gecilmedi(gecilmedi) {
    if (document.documentElement.hasAttribute('data-eos-atc-gecilmedi') === gecilmedi) return;
    document.documentElement.toggleAttribute('data-eos-atc-gecilmedi', gecilmedi);
  }

  /** @param {string} height */
  #publishHeight(height) {
    if (this.#publishedHeight === height) return;
    this.#publishedHeight = height;
    document.documentElement.style.setProperty('--eos-sticky-sepet-h', height);
  }

  #renderButton() {
    // `data-tek-etiket`: always "Sepete ekle"; an incomplete personalisation
    // still opens the step-by-step flow on click (see tikla()).
    const label = this.#complete || this.dataset.tekEtiket === 'true' ? this.dataset.etiketSepet : this.dataset.etiketKisisellestir;
    if (label && this.refs.buton.textContent !== label) this.refs.buton.textContent = label;
    const soldOut = this.#addToCartButton()?.disabled ?? false;
    this.refs.buton.disabled = this.#complete && soldOut;
  }

  #syncPrice() {
    const source = this.#mainSection()?.querySelector('product-price .price');
    // Visible amount only: the theme's screen-reader prefix ("İndirimli fiyat")
    // made the text 167 px wide and pushed it under the button on a phone.
    let text = '';
    if (source) {
      const copy = /** @type {HTMLElement} */ (source.cloneNode(true));
      for (const hidden of copy.querySelectorAll('.visually-hidden')) hidden.remove();
      text = (copy.textContent ?? '').replace(/\s+/g, ' ').trim();
    }
    if (this.refs.fiyat.textContent !== text) this.refs.fiyat.textContent = text;
    this.#renderButton();
  }
}

if (!customElements.get('eos-sticky-sepet-component')) {
  customElements.define('eos-sticky-sepet-component', EosStickySepet);
}
